import './context-modal.scss';

import {
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { formatTestId } from '../../data/utils/string.utils';
import {
  ContextMenuHorizontalPosition,
  ContextMenuState,
  ContextMenuVerticalPosition,
} from '../../data/utils/use-context-menu';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';

const MARGIN = 20;
const TRESHHOLD = 5;
const MAXHEIGHT = 722;

const getVerticalPosition = (state: ContextMenuState) => {
  let top = 0;
  let maxHeight = 0;

  if (state.data.vertical === ContextMenuVerticalPosition.Top) {
    top = MARGIN;
    maxHeight = state.y - state.height / 2 - TRESHHOLD - MARGIN;
  } else {
    top = state.y + state.height / 2 + TRESHHOLD;
    maxHeight = window.innerHeight - top - MARGIN;
  }

  return {
    top: `${top}px`,
    maxHeight: `${Math.min(maxHeight, MAXHEIGHT)}px`,
  };
};

const getHorizontalPosition = (state: ContextMenuState, element: DOMRect) => {
  let left = 0;

  if (state.data.horizontal === ContextMenuHorizontalPosition.Right) {
    left = Math.min(
      state.x - state.width / 2,
      window.innerWidth - MARGIN - element.width
    );
  } else {
    left = Math.max(state.x + state.width / 2 - element.width, MARGIN);
  }

  return {
    left: `${left}px`,
  };
};

type ContextModalProps = PropsWithChildren<{
  testId?: string;
  state: ContextMenuState | false;
  className?: string;
  onClose?: () => void;
}>;

export function ContextModal({
  testId,
  state,
  className,
  onClose,
  ...props
}: ContextModalProps) {
  const container = document.getElementById('portal-container');

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('context-modal', testId, testIdType),
    [testId]
  );

  const [style, setStyle] = useState<{
    top: string;
    left: string;
    opacity: number;
  }>();

  useEffect(() => {
    if (state) {
      const refElement = (state.ref as RefObject<HTMLDivElement>)?.current;

      if (refElement) {
        const element = refElement.getBoundingClientRect();

        setStyle({
          opacity: 1,
          ...getVerticalPosition(state),
          ...getHorizontalPosition(state, element),
        });
      }
    }

    return () => {
      setStyle(undefined);
    };
  }, [state]);

  const onHandleClose = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      onClose?.();
    },
    [onClose]
  );

  if (!container || !state) return null;

  return ReactDOM.createPortal(
    <div
      data-test-id={getTestId()}
      className={`context-modal${
        className ? ` context-modal--${className}` : ``
      }`}
      style={style}
      ref={state.ref}
    >
      {onClose && (
        <button
          data-test-id={getTestId('close')}
          type="button"
          className="close"
          onClick={onHandleClose}
        >
          <CustomFormattedMessage id="common.close" />
        </button>
      )}

      {props.children && (
        <div
          data-test-id={getTestId('content')}
          className="context-modal__content"
        >
          {props.children}
        </div>
      )}
    </div>,
    container
  );
}
